import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { activateDevice } from '@/features/auth/api/activateDevice'
import {
  formatActivationCodeInput,
  isValidActivationCode,
  stripActivationCode,
} from '@/features/auth/activationCode'
import { warehouseTerms } from '@/features/warehouse/warehouseTerms'
import { getDeviceOs, getOrCreateDeviceUid } from '@/shared/auth/deviceUid'
import { setWarehouseDevice } from '@/shared/auth/warehouseDeviceStorage'
import { clearWarehouseSession } from '@/shared/auth/warehouseSessionStorage'

type SubmitState = 'idle' | 'verifying' | 'success' | 'error'

export function WarehouseActivatePage() {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [submitState, setSubmitState] = useState<SubmitState>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const isVerifying = submitState === 'verifying'
  const isSuccess = submitState === 'success'
  const isDisabled = isVerifying || isSuccess
  const codeComplete = isValidActivationCode(code)
  const canActivate = codeComplete && !isDisabled

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    if (!canActivate) return

    setSubmitState('verifying')
    try {
      const res = await activateDevice({
        activation_code: stripActivationCode(code),
        device_uid: getOrCreateDeviceUid(),
        os: getDeviceOs(),
      })

      if (res.device_type !== 'warehouse_tablet' || !res.warehouse_id) {
        throw new Error(warehouseTerms.activation.wrongDeviceType)
      }

      setWarehouseDevice({
        deviceId: res.device_id,
        deviceUid: res.device_uid,
        deviceName: res.device_name,
        brandId: res.brand_id,
        warehouseId: res.warehouse_id,
        warehouseCode: res.warehouse_code,
        warehouseName: res.warehouse_name ?? '—',
        deviceType: 'warehouse_tablet',
        activatedAt: new Date().toISOString(),
      })
      clearWarehouseSession()
      setSubmitState('success')
      setTimeout(() => navigate('/warehouse/login', { replace: true }), 600)
    } catch (err) {
      setSubmitState('error')
      setSubmitError(err instanceof Error ? err.message : warehouseTerms.activation.activateFailed)
    }
  }

  return (
    <main className="w-full max-w-[480px]">
      <div className="bg-surface-card rounded-xl border border-outline-variant shadow-sm overflow-hidden flex flex-col">
        <div className="bg-surface pt-8 pb-6 px-gutter-tablet sm:px-gutter-desktop flex flex-col items-center border-b border-surface-variant">
          <div className="w-16 h-16 rounded-full bg-primary-container flex items-center justify-center mb-4 text-on-primary-container shadow-sm border border-outline-variant/30">
            <span className="material-symbols-outlined text-[32px] icon-fill">warehouse</span>
          </div>
          <h1 className="text-headline-md text-primary mb-2 text-center">{warehouseTerms.appName}</h1>
          <h2 className="text-headline-lg-mobile text-on-surface text-center">{warehouseTerms.activation.title}</h2>
        </div>

        <div className="p-gutter-tablet sm:p-gutter-desktop flex flex-col gap-6">
          <p className="text-body-sm text-on-surface-variant text-center leading-relaxed">
            {warehouseTerms.activation.subtitle}
          </p>

          <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-3">
              <label htmlFor="activation_code" className="field-label flex items-center justify-between">
                <span>{warehouseTerms.activation.codeLabel}</span>
                <span className="material-symbols-outlined text-base text-on-surface-variant" title="Input aman">
                  lock
                </span>
              </label>
              <input
                id="activation_code"
                name="activation_code"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                autoFocus
                spellCheck={false}
                disabled={isDisabled}
                value={code}
                onChange={(e) => {
                  setCode(formatActivationCodeInput(e.target.value))
                  setSubmitState('idle')
                  setSubmitError(null)
                }}
                placeholder="WH7K9M"
                maxLength={6}
                className="input-code"
                aria-describedby="warehouse_code_helper"
                aria-invalid={submitState === 'error'}
              />
              <p id="warehouse_code_helper" className="text-label-md text-on-surface-variant text-center">
                {warehouseTerms.activation.codeHelper}
              </p>
            </div>

            {submitError && (
              <div role="alert" className="alert-error">
                <span className="material-symbols-outlined text-base shrink-0">error</span>
                <span>{submitError}</span>
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={!canActivate}
                className={`btn-cta btn-tactile ${isSuccess ? 'is-success' : ''}`}
              >
                {isVerifying && (
                  <>
                    <span className="material-symbols-outlined animate-spin">progress_activity</span>
                    {warehouseTerms.activation.verifying}
                  </>
                )}
                {isSuccess && (
                  <>
                    <span className="material-symbols-outlined">check_circle</span>
                    {warehouseTerms.activation.success}
                  </>
                )}
                {!isVerifying && !isSuccess && (
                  <>
                    {warehouseTerms.activation.cta}
                    <span className="material-symbols-outlined font-bold">arrow_forward</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-surface-container py-4 px-gutter-tablet sm:px-gutter-desktop text-center border-t border-surface-variant">
          <p className="text-label-md text-on-surface-variant flex items-center justify-center gap-2">
            <span className="material-symbols-outlined text-sm">support_agent</span>
            {warehouseTerms.activation.footer}
          </p>
        </div>
      </div>
    </main>
  )
}
